import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PatientContact } from 'src/app/models/patient.model';
import { DataService } from 'src/app/services/data.service';
import { UtilsService } from 'src/app/services/utils.service';
import { phoneNumberValidator } from 'src/app/shared/validators/phone-number.validator';

@Component({
  selector: 'app-contact',
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent implements OnInit {
  @Input()
  contact: PatientContact;
  @Input()
  expandPanel = false;
  @Input()
  smsConsentEnabled = true;
  @Output()
  toSaveContact = new EventEmitter<PatientContact>();
  @Output()
  toDeleteContact = new EventEmitter<PatientContact>();
  contactForm: UntypedFormGroup;
  phoneTypes: any[] = [];

  readonly DAYS = [
    { key: 'mon', label: 'M' },
    { key: 'tue', label: 'T' },
    { key: 'wed', label: 'W' },
    { key: 'thu', label: 'T' },
    { key: 'fri', label: 'F' },
    { key: 'sat', label: 'S' },
    { key: 'sun', label: 'S' },
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private dataService: DataService,
    private utilService: UtilsService
  ) {
    this.contactForm = this.fb.group({
      contact_data: ['', [Validators.required, phoneNumberValidator]],
      contact_type: ['', Validators.required],
      contact_description: [''],
      icon_name: [''],
      isPrimary: [false],
      isPrimaryForSms: [false],
      best_times_days: [[]],
      best_times_period: [[]],
    });
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  initializeForm() {
    this.phoneTypes = Object.values(this.dataService.phoneTypes);
    if (this.contact) {
      const rawPeriod = this.contact.best_times_period;
      const normalizedContact = {
        ...this.contact,
        best_times_period: Array.isArray(rawPeriod) ? rawPeriod : rawPeriod ? [rawPeriod as string] : [],
      };
      this.contactForm.patchValue(normalizedContact);
      if (this.contact.contact_data) {
        this.contactForm.get('contact_data').setValue(this.getNormalizedPhoneValue(this.contact.contact_data));
      }
      if (this.contact.contact_type) {
        this.contactForm
          .get('contact_type')
          .setValue(this.phoneTypes.find((phoneType) => phoneType.value === this.contact.contact_type));
      }
    }
    if (!this.smsConsentEnabled) {
      this.contactForm.get('isPrimaryForSms').disable();
    }
  }

  saveContact(): void {
    this.contact = this.buildContactData();
    this.toSaveContact.emit(this.contact);
  }

  deleteContact(): void {
    this.contact = this.buildContactData();
    this.toDeleteContact.emit(this.contact);
  }

  getIconFromType(contactType: string): string {
    return this.dataService.getIconFromType(contactType);
  }

  isDaySelected(day: string): boolean {
    const days: string[] = this.contactForm.get('best_times_days').value || [];
    return days.includes(day);
  }

  toggleDay(day: string): void {
    const days: string[] = [...(this.contactForm.get('best_times_days').value || [])];
    const idx = days.indexOf(day);
    if (idx > -1) {
      days.splice(idx, 1);
    } else {
      days.push(day);
    }
    this.contactForm.get('best_times_days').setValue(days);
  }

  isPeriodSelected(period: string): boolean {
    const periods: string[] = this.contactForm.get('best_times_period').value || [];
    return periods.includes(period);
  }

  togglePeriod(period: 'morning' | 'afternoon'): void {
    const periods: string[] = [...(this.contactForm.get('best_times_period').value || [])];
    const idx = periods.indexOf(period);
    if (idx > -1) {
      periods.splice(idx, 1);
    } else {
      periods.push(period);
    }
    this.contactForm.get('best_times_period').setValue(periods);
  }

  private buildContactData(): PatientContact {
    return {
      ...this.contactForm.getRawValue(),
      id: this.contact && this.contact.doc && this.contact.doc.id ? this.contact.doc.id : undefined,
      contact_data: `+1${this.contactForm.get('contact_data').value.replace(/-/g, '')}`,
      contact_type: this.contactForm.get('contact_type').value.value,
      contact_description: this.contactForm.get('contact_description').value,
    };
  }

  private getNormalizedPhoneValue(phone: string) {
    const phoneWithoutSign = phone.substring(2).trim();
    const phoneNormalized = this.utilService.getPhoneNumberFormated(phoneWithoutSign);
    return phoneNormalized;
  }
}
